import React from 'react';
import Seo from '../components/Seo';
import { blogPostLd, breadcrumbLd } from '../lib/seo';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, FileText, MessageCircle } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { insights, getInsight } from '../data/insights';
import { openWhatsApp } from '../lib/wa';

const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const Block = ({ block }) => {
  if (block.t === 'h2') return <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ktc-green mt-10 mb-4">{block.c}</h2>;
  if (block.t === 'ul') return (
    <ul className="my-5 space-y-2.5">
      {block.c.map((li, i) => (
        <li key={i} className="flex gap-3 text-ktc-charcoal/80 leading-relaxed">
          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-ktc-gold shrink-0" />
          <span>{li}</span>
        </li>
      ))}
    </ul>
  );
  return <p className="text-ktc-charcoal/80 leading-relaxed my-4 text-[16.5px]">{block.c}</p>;
};

const InsightsPost = () => {
  const { slug } = useParams();
  const article = getInsight(slug);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-ktc-green">Article not found</h1>
        <Link to="/insights" className="inline-flex items-center gap-2 text-ktc-gold font-medium mt-6"><ArrowLeft size={16} /> Back to insights</Link>
      </div>
    );
  }

  const related = insights.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div>
      <Seo
        title={article.title}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        image={article.image}
        type="article"
        jsonLd={[
          blogPostLd(article, article.image),
          breadcrumbLd([
            { name: 'Home', path: '/' },
            { name: 'Insights', path: '/insights' },
            { name: article.title, path: `/insights/${article.slug}` },
          ]),
        ]}
      />
      <PageHero title={article.title} crumb="Insights" />

      <article className="max-w-3xl mx-auto px-6 py-14">
        <div className="flex items-center gap-4 text-sm text-ktc-charcoal/60">
          <span className="bg-ktc-cream text-ktc-green font-semibold px-3 py-1 rounded-full text-xs">{article.category}</span>
          <span className="flex items-center gap-1.5"><Calendar size={14} /> {fmt(article.date)}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} /> {article.readTime}</span>
        </div>

        <Reveal className="img-zoom rounded-2xl overflow-hidden shadow-xl mt-7">
          <img src={article.image} alt={article.title} className="w-full h-[340px] object-cover" />
        </Reveal>

        <div className="mt-8">
          {article.body.map((block, i) => <Block key={i} block={block} />)}
        </div>

        {/* CTA */}
        <div className="mt-12 bg-ktc-green text-ktc-ivory rounded-2xl p-8" data-testid="insight-cta">
          <h3 className="font-serif text-2xl font-semibold">Sourcing rice from Pakistan?</h3>
          <p className="text-ktc-ivory/80 mt-2 text-[15px]">Kassam Trading Company mills and exports the full range of basmati and non-basmati rice, salt and grains. Tell us your requirement and destination port.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-goldLight hover:bg-ktc-gold text-ktc-greenDark font-semibold px-6 py-3 rounded-md transition-colors">
              <FileText size={18} /> Request a Quote
            </Link>
            <button type="button" onClick={() => openWhatsApp()} data-testid="insight-whatsapp-button" className="inline-flex items-center gap-2 border border-ktc-ivory/30 hover:border-ktc-ivory text-ktc-ivory font-medium px-6 py-3 rounded-md transition-colors">
              <MessageCircle size={18} /> WhatsApp Us
            </button>
            <Link to="/products" className="inline-flex items-center gap-2 text-ktc-goldLight font-medium px-3 py-3 hover:gap-3 transition-all">
              View products <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="bg-ktc-cream/50">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="font-serif text-3xl font-semibold text-ktc-green mb-8">More Insights</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} className="group bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream card-lift">
                <div className="img-zoom h-40"><img src={a.image} alt={a.title} className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <h3 className="font-serif text-lg font-semibold text-ktc-green leading-snug">{a.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-ktc-gold font-medium text-sm mt-3 group-hover:gap-2.5 transition-all">Read guide <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPost;
